/*
 * CartRegions — the one place that knows which cart regions exist on the current
 * page, how to swap them, and how to keep their empty-state flags honest.
 *
 * ── WHY THIS EXISTS ───────────────────────────────────────────────────────────
 * The cart has two surfaces and they render independently. <cart-drawer-items>
 * extends CartItems (cart-drawer.js), but each subclass used to carry its own
 * getSectionsToRender() naming only ITS OWN regions:
 *
 *   CartItems       -> main-cart-items, cart-icon-bubble, cart-live-region-text,
 *                      main-cart-footer          (no #CartDrawer)
 *   CartDrawerItems -> CartDrawer, cart-icon-bubble
 *                                                (no main-cart-items/-footer)
 *
 * The drawer renders on EVERY page (layout/theme.liquid:364-365), so on /cart
 * both surfaces are live at once and a quantity change on either one left the
 * other showing pre-change markup. The cross-surface event channel cannot fix
 * that on its own: updateQuantity signs off with cartUpdate {source:'cart-items'}
 * (cart.js) and the subscriber both classes share early-returns on exactly that
 * source, by design — it is the "already rendered, do not re-fetch" flag.
 *
 * cart-attach-add.js had already solved this for the attach band's Add button by
 * asking for every region present on the page in one round trip. That rule now
 * lives here instead of in three places, because three copies is how the next
 * cart region added drifts back into the same bug.
 *
 * ── ONE ROUND TRIP, NOT TWO ───────────────────────────────────────────────────
 * Nothing here adds a request. The Section Rendering API call is already in
 * flight for every cart mutation; this only widens the `sections` list it
 * carries, and every entry is gated on its target existing so the drawer's copy
 * running on a PDP asks for exactly what it asked for before.
 *
 * ── WHY .drawer__inner AND NOT #CartDrawer ────────────────────────────────────
 * #CartDrawer-Overlay is inside #CartDrawer but OUTSIDE .drawer__inner
 * (snippets/cart-drawer.liquid:61-63). Swapping .drawer__inner leaves the
 * overlay node — and the click handler cart-drawer.js's constructor bound to it —
 * attached, so no re-bind is needed. The drawer's close buttons use inline
 * onclick="this.closest('cart-drawer').close()" and survive either way.
 * cart-attach-add.js used #CartDrawer and re-bound the overlay by hand; that
 * re-bind is gone with this change.
 *
 * ── THE FIVE-SECTION CAP (review R-06) ───────────────────────────────────────
 * The Section Rendering API renders at most five sections per request. Ask for
 * six and it does not error: the write lands, HTTP 200, and `sections` comes
 * back null — every region stays stale, silently (measured 2026-09-28). So the
 * list is capped here, a null payload is warned about, and a region whose OWN
 * entry is null (its id no longer exists on the serving theme — a template
 * section after a mid-session theme publish) can take a spec-level fallback.
 *
 * ── ONE GET OF EVERY REGION (fetchSections / fetchAndRender) ─────────────────
 * The fallback for every case where a write's own response cannot be trusted
 * to carry the regions: a null payload (above), a write error (the page may be
 * stale, review R-21), a write that raced another writer's (R-10), a bfcache
 * restore or a tab coming back to the front (§2.4.7, R-21). Rendered from this
 * page's own path, because a template section only renders in its page's
 * context (routes.cart_url would lose the PDP's `product`).
 *
 * Globals used: none beyond DOM. Loaded with defer from snippets/cart-drawer.liquid
 * and sections/main-cart-items.liquid, so on /cart it is tagged twice — hence the
 * if (!window.CartRegions) guard, same pattern as cart-attach-add.js.
 * Consumers: cart.js, cart-attach-add.js, a4-cart.js, a4-party-pack.js,
 * component-product-card.js.
 */
if (!window.CartRegions) {
  const MAX_SECTIONS = 5;

  /*
   * Every cart region the theme can put on a page, in render order.
   *   id        the element on the live page to write into
   *   section   the Section Rendering API id, or fromDataId when the template
   *             names it (templates/cart.json calls main-cart-items "cart-items"
   *             and main-cart-footer "cart-footer", so those are read off
   *             data-id and never hardcoded)
   *   selector  the sub-node to swap, in both the target and the response
   *
   * cart-live-region-text and cart-icon-bubble are bare one-liner sections; the
   * API wraps them in .shopify-section, and their live targets have no such
   * child, so the swap falls through to the target itself. That is Dawn's
   * existing behaviour, preserved.
   *
   * A4PartyPack is the ScribblePop party-pack band (docs/SCRIBBLEPOP_A4_BUILD.md
   * §2.4.1). It is a TEMPLATE section, so the API renders it from sections_url —
   * every caller already sends window.location.pathname. The gate below drops it
   * on every page without #A4PartyPack. It must never share a page with
   * main-cart-items: /cart already fills Shopify's five-section cap on its own,
   * and the cap keeps the last spec — this one — off the list if it ever did.
   *
   * whenNull: the band's section id is the template's. After a theme publish
   * changes it mid-session, the API answers null for it on every write while the
   * drawer and the bubble keep moving (review §3.3 B4). A band that can no longer
   * be rendered must not keep a stale count on screen, so it takes its own "off"
   * state, which its CSS hides with the waves; the next page load serves the
   * new id. The state lives on .a4-party-pack__inner, the one node a swap
   * refreshes, so setting it there is exactly what a real "off" render does.
   */
  const REGION_SPECS = [
    { id: 'main-cart-items', fromDataId: true, selector: '.js-contents' },
    { id: 'main-cart-footer', fromDataId: true, selector: '.js-contents' },
    { id: 'cart-live-region-text', section: 'cart-live-region-text', selector: '.shopify-section' },
    { id: 'CartDrawer', section: 'cart-drawer', selector: '.drawer__inner' },
    { id: 'cart-icon-bubble', section: 'cart-icon-bubble', selector: '.shopify-section' },
    {
      id: 'A4PartyPack',
      fromDataId: true,
      selector: '.js-contents',
      whenNull(target) {
        const inner = target.querySelector('[data-a4-state]');
        if (inner) inner.dataset.a4State = 'off';
      },
    },
  ];

  window.CartRegions = {
    /*
     * The regions actually on this page. Gating matters twice over: the drawer
     * runs on every template, and cart.js's render loop has no null guard — one
     * ungated entry would throw on every PDP.
     */
    sectionsToRender() {
      const list = REGION_SPECS.reduce((entries, spec) => {
        const target = document.getElementById(spec.id);
        if (!target) return entries;
        const section = spec.fromDataId ? target.dataset.id : spec.section;
        if (!section) return entries;
        entries.push({ id: spec.id, section: section, selector: spec.selector });
        return entries;
      }, []);
      // Past five the API answers null for the WHOLE list (R-06): better one region
      // that never updates, loudly, than every region stale on every write.
      if (list.length > MAX_SECTIONS) {
        const dropped = list.splice(MAX_SECTIONS).map((entry) => entry.id);
        console.warn(
          `CartRegions: ${dropped.join(', ')} left off the render list — the Section Rendering API renders at most ${MAX_SECTIONS} sections per request and returns null for all of them past that. Those regions will not update on cart writes; take one region off this page.`
        );
      }
      return list;
    },

    /*
     * `sections` is the Section Rendering API payload keyed by section id —
     * parsedState.sections from /cart/change.js, response.sections from
     * /cart/add.js, or the whole body of a ?sections= GET. Entries whose markup
     * did not come back are skipped rather than throwing, so a partial response
     * degrades to "that region is stale" instead of "the handler died and the
     * spinner never clears". Two null shapes are told apart (R-06): a null
     * PAYLOAD (the list was refused — warned, the caller refetches if it can)
     * and a null ENTRY (that one id is unknown to the serving theme — the spec's
     * whenNull runs, if it has one).
     */
    renderSections(list, sections) {
      if (!sections) {
        console.warn(
          'CartRegions: the response carried no sections, so every region on this page is stale until the next write or a refetch (review R-06).'
        );
        return;
      }
      list.forEach((entry) => {
        const target = document.getElementById(entry.id);
        if (!target) return;
        const html = sections[entry.section];
        if (html === null) {
          const spec = REGION_SPECS.find((candidate) => candidate.id === entry.id);
          if (spec && typeof spec.whenNull === 'function') spec.whenNull(target);
          return;
        }
        if (typeof html !== 'string') return;
        const source = new DOMParser().parseFromString(html, 'text/html').querySelector(entry.selector);
        if (!source) return;
        const destination = target.querySelector(entry.selector) || target;
        destination.innerHTML = source.innerHTML;
      });
    },

    /*
     * One GET of every listed region from this page's own path. Resolves to the
     * payload (renderSections' shape); rejects on a network failure, a non-2xx
     * (the bot wall answers 429 with HTML) or a non-JSON body, so callers decide
     * what a failed refetch means. Pathname only, no query — the same context
     * every write already renders in.
     */
    fetchSections(list) {
      // The same cap as sectionsToRender: past five the GET fails the same way.
      const ids = list.slice(0, MAX_SECTIONS).map((entry) => entry.section).join(',');
      return fetch(`${window.location.pathname}?sections=${ids}`).then((response) => {
        if (!response.ok) throw new Error(`CartRegions: refetch answered ${response.status}`);
        return response.json();
      });
    },

    /* fetchSections, then the swap. Resolves to the payload. */
    fetchAndRender(list) {
      return this.fetchSections(list).then((sections) => {
        this.renderSections(list, sections);
        return sections;
      });
    },

    /*
     * MUST be called AFTER renderSections, and it re-queries every node, because
     * the .drawer__inner swap destroys and recreates <cart-drawer-items>.
     *
     * These four flags are load-bearing, not cosmetic: component-cart.css:7-14
     * is what hides .cart__contents / .cart__footer and reveals .cart__empty-text,
     * and component-cart-drawer.css:196 is what hides the drawer's checkout
     * footer. They sit OUTSIDE every swapped region, so no server render can set
     * them — except on <cart-drawer-items>, which is recreated by the swap and so
     * arrives already correct. Setting it again there is a harmless no-op, and
     * cheaper than reasoning about which of the four self-heal.
     *
     * The old code toggled these on `this`, which meant a removal made from the
     * drawer never set is-empty on <cart-items>: the cart page would render its
     * empty markup while still laid out as a full cart.
     */
    syncEmptyState(isEmpty) {
      ['cart-items', 'cart-drawer', 'cart-drawer-items'].forEach((tag) => {
        const node = document.querySelector(tag);
        if (node) node.classList.toggle('is-empty', isEmpty);
      });
      const footer = document.getElementById('main-cart-footer');
      if (footer) footer.classList.toggle('is-empty', isEmpty);
    },

    /*
     * After a GET refetch there is no item_count to read, so the flag comes off
     * the fresh DOM: the band's inner carries data-cart-count, and the drawer's
     * <cart-drawer-items>, recreated by the swap, carries is-empty itself
     * (snippets/cart-drawer.liquid). The drawer is on every page, so one of the
     * two is always there. Nothing found: leave the flags as they are.
     */
    syncEmptyStateFromDom() {
      const inner = document.querySelector('#A4PartyPack [data-cart-count]');
      if (inner) return this.syncEmptyState(inner.dataset.cartCount === '0');
      const drawerItems = document.querySelector('cart-drawer-items');
      if (drawerItems) this.syncEmptyState(drawerItems.classList.contains('is-empty'));
    },
  };
}
